import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { RecurrentToggle } from '@/components/recurrent-toggle';
import { SubscriptionCard } from '@/components/SubscriptionCard';
import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PayLogo } from '@/components/ui/pay-logo';
import { getWebApp, haptic, openInvoiceUrl, setMainButtonText } from '@/lib/telegram';
import { rub } from '@/lib/currency';
import { api, isProviderError, type RenewQuote } from '@/lib/api';
import { providerLabel } from '@/lib/providers';
import { SbpRow } from '@/components/ui/provider-pills';
import { SBP_GROUP, useSbpGroup } from '@/lib/sbp-group';
import { minorToAmount } from '@/lib/currency';
import { Card, CardContent } from '@/components/ui/card';
import { IconChip, SectionLabel, OptionRow, OptionGroup } from '@/components/ui/calm';
import { Skeleton, SkeletonCard, SkeletonLine, SkeletonPage } from '@/components/ui/skeleton';
import { Snowflake, Zap, Wallet, CalendarClock } from 'lucide-react';
import { toast } from 'sonner';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

export const Route = createFileRoute('/renew/$subscriptionId')({
  component: Renew,
});


function Renew() {
  const { subscriptionId } = Route.useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const { data: subs, isPending: subsPending } = useQuery({
    queryKey: ['subs'],
    queryFn: () => api.allSubscriptions(),
  });
  // Истёкшую подписку продлевают с главной и из прогрева: в списке живых её уже
  // нет, а без неё экран рисовал «VPN» вместо тарифа и не узнавал пробник.
  const { data: expiredSubs, isPending: expiredPending } = useQuery({
    queryKey: ['subs-expired'],
    queryFn: () => api.expiredSubscriptions(),
  });
  const sub =
    subs?.find((s) => s.id === subscriptionId) ??
    expiredSubs?.find((s) => s.id === subscriptionId) ??
    null;
  const isFrozen = sub?.status === 'frozen';
  const isTrial = sub?.isTrial ?? false;
  // «Навсегда» не продлевается вовсе, а её пробник ведёт в раздел «Навсегда».
  const isLifetime = sub?.isLifetime ?? false;
  const lifetimeSection = sub?.supplier === 'scvpn';
  const { data: providersData } = useQuery({
    queryKey: ['payment-providers'],
    queryFn: () => api.paymentProviders(),
  });
  // Баланс клиента + цена продления. Цена — из актуального списка планов (та же
  // текущая наценка партнёра, по которой re-quote'ит renew_from_balance на бэке),
  // матчим по plan_uuid. Так на фронте знаем, хватает ли баланса, ещё до запроса.
  const { data: profile } = useQuery({ queryKey: ['profile'], queryFn: () => api.profile() });
  const { data: plans, isPending: plansPending } = useQuery({ queryKey: ['plans'], queryFn: () => api.plans() });
  const subPlan = plans?.find((p) => p.id === sub?.planId) ?? null;
  const renewPriceRub = subPlan?.priceRub ?? null;
  // Спецтариф продлевать нельзя: он покупается один раз, продление обошло бы лимит.
  // Бэк отбивает такое продление 409, тут просто не доводим до оплаты.
  const isSpecial = subPlan?.isSpecial ?? false;
  const balanceDisplay = (profile?.balance_display_minor ?? 0) / 100;

  // Срок продления. null — период тарифа (как было всегда и как открывается экран).
  const [days, setDays] = useState<number | null>(null);
  const [customInput, setCustomInput] = useState('');
  const [debouncedDays, setDebouncedDays] = useState<number | null>(null);

  useEffect(() => {
    // Ждём, пока человек допечатает: иначе на «45» уходит три запроса, и цена
    // прыгает на глазах.
    const timer = setTimeout(() => setDebouncedDays(days), 350);
    return () => clearTimeout(timer);
  }, [days]);

  // Первый способ из списка сервера (СБП), а не CryptoBot: см. checkout.$planId.tsx.
  const [provider, setProvider] = useState<string | null>(null);

  const quoteQ = useQuery({
    queryKey: ['renew-quote', subscriptionId, debouncedDays],
    queryFn: () => api.renewQuote(subscriptionId, debouncedDays),
    enabled: !isTrial && !isSpecial,
    staleTime: 30_000,
  });
  // Цена устарела, пока летит запрос за новой: показывать старую сумму рядом с
  // кнопкой оплаты нельзя — человек нажмёт на число, которого уже нет.
  const quoteStale = days !== debouncedDays || quoteQ.isFetching;
  const quote = quoteQ.data ?? null;
  // Партнёр мог скрыть семейство этой подписки: её тарифа тогда нет в каталоге, а
  // продлевать её можно, скрытие продление не трогает. Цену в этом случае знает только
  // ответ бэка, поэтому «с баланса» разрешаем и по нему.
  const balancePayable =
    !isTrial &&
    !isSpecial &&
    !isFrozen &&
    ((quote?.price_display_minor ?? 0) > 0 || (renewPriceRub ?? 0) > 0);
  // Сбор за оплату берётся один раз, на входе денег: счётом платим со сбором,
  // с баланса — без него, он уже уплачен при пополнении.
  const balancePriceDisplay = quote
    ? Math.ceil(quote.price_display_minor / 100)
    : renewPriceRub;
  // Сумма счёта выбранного способа со сбором, посчитанная сервером той же формулой,
  // что и счёт продления. Без неё кнопка обещала цену без сбора способа.
  const invoiceMinor =
    provider && provider !== 'balance' ? quote?.provider_totals?.[provider] : undefined;
  const invoicePriceDisplay =
    invoiceMinor != null
      ? minorToAmount(invoiceMinor)
      : quote
        ? String(Math.ceil((quote.total_display_minor || quote.price_display_minor) / 100))
        : renewPriceRub != null
          ? String(renewPriceRub)
          : null;
  const priceDisplay =
    provider === 'balance'
      ? balancePriceDisplay != null
        ? String(balancePriceDisplay)
        : null
      : invoicePriceDisplay;
  const quoteReady = quote != null && !quoteStale;
  // Автосписание у кассы: только при продлении на период тарифа (сервер отдаёт
  // `recurrent` лишь для него) и только для способов из его списка.
  const [recurrentOn, setRecurrentOn] = useState(true);
  const recurrentOffer = days === null && !quoteStale ? (quote?.recurrent ?? null) : null;
  const recurrentShown =
    !!recurrentOffer && !!provider && recurrentOffer.providers.includes(provider);
  const autoRenewOn = recurrentShown && recurrentOn;
  // Подтверждение списания называет, что именно купят: тариф, срок и сумму.
  const balanceConfirmText = t('Renew balance confirm', {
    plan: sub?.planName ?? 'VPN',
    days: t('Count days', { count: quote?.days ?? quote?.plan_days ?? 0 }),
    amount: rub(balancePriceDisplay ?? 0),
  });

  // Цена и достаточность баланса — из ответа бэка по выбранному сроку. Считать это
  // на фронте нельзя: наценка бота, скидка промокода, курс и округление живут там.
  const balanceDisplayRub = quote ? quote.balance_display_minor / 100 : balanceDisplay;
  const balanceEnough = balancePayable && (quote ? quote.balance_enough : false);
  const balanceShortfallRub =
    balancePriceDisplay != null
      ? Math.max(0, Math.ceil(balancePriceDisplay - balanceDisplayRub))
      : 0;

  useEffect(() => {
    // 'balance' — валидный выбор, но его нет в providersData.providers (там только
    // внешние провайдеры) → исключаем, иначе эффект мгновенно откатит выбор баланса.
    if (!providersData || provider === 'balance') return;
    const list = providersData.providers;
    if (provider === null || !list.includes(provider)) setProvider(list[0] ?? null);
  }, [providersData, provider]);

  // Выбран баланс, но он перестал быть доступным (не хватает/разморозили) — сбросим.
  useEffect(() => {
    if (provider === 'balance' && !balanceEnough) {
      setProvider(providersData?.providers[0] ?? null);
    }
  }, [provider, balanceEnough, providersData]);

  // Замок на повторный тап по MainButton — см. checkout.$planId.tsx.
  const renewBusyRef = useRef(false);

  const { mutate: doRenew, isPending } = useMutation({
    mutationFn: () =>
      api.renewSubscription(
        subscriptionId,
        provider ?? '',
        debouncedDays,
        recurrentShown && recurrentOn,
      ),
    onSettled: () => {
      renewBusyRef.current = false;
    },
    onSuccess: (intent) => {
      openInvoiceUrl(intent.invoice_url);
      navigate({ to: '/processing/$paymentId', params: { paymentId: intent.payment_id } });
    },
    onError: (err: Error) => {
      haptic('error');
      toast.error(err.message || t('Renew invoice failed'));
    },
  });

  const { mutate: doRenewBalance, isPending: renewingBalance } = useMutation({
    mutationFn: () => api.renewFromBalance(subscriptionId, debouncedDays),
    onSettled: () => {
      renewBusyRef.current = false;
    },
    onSuccess: async () => {
      haptic('success');
      await qc.invalidateQueries();
      toast.success(t('Renewed from balance'));
      navigate({ to: '/me' });
    },
    onError: (err: Error) => {
      haptic('error');
      toast.error(
        isProviderError(err) ? t('Provider unavailable') : err.message || t('Renew balance failed'),
      );
    },
  });

  useEffect(() => {
    const tg = getWebApp();
    if (!tg?.BackButton || !tg?.MainButton) return;
    tg.BackButton.show();
    const onBack = () => navigate({ to: '/subscription' });
    tg.BackButton.onClick(onBack);

    // «Навсегда» продлевать не нужно: экран только объясняет это, платить нечего.
    if (isLifetime) {
      tg.MainButton.hide();
      return () => {
        tg.BackButton.offClick(onBack);
      };
    }

    // Триал и спецтариф продлевать нельзя — MainButton ведёт в выбор тарифа. Раньше
    // вёл на апгрейд, а там для трёхдневного пробника вариантов нет, и человек читал
    // «дождитесь окончания подписки и купите новую» ровно тогда, когда хотел платить.
    if (isTrial || isSpecial) {
      setMainButtonText(t('Go paid caps'));
      tg.MainButton.show();
      tg.MainButton.enable();
      const onChoosePlan = () =>
        navigate({ to: '/plans', search: lifetimeSection ? { section: 'lifetime' } : {} });
      tg.MainButton.onClick(onChoosePlan);
      return () => {
        tg.BackButton.offClick(onBack);
        tg.MainButton.offClick(onChoosePlan);
      };
    }

    // Способ оплаты ещё не выбран (список едет) — кнопку не показываем вовсе.
    if (provider === null) {
      tg.MainButton.hide();
      return () => {
        tg.BackButton.offClick(onBack);
      };
    }

    // Оплата с баланса — синхронное списание, без инвойса.
    if (provider === 'balance') {
      setMainButtonText(t('Pay from balance caps', { price: priceDisplay ?? 0 }));
      tg.MainButton.show();
      const onMainBal = async () => {
        if (renewBusyRef.current || !quoteReady) return;
        renewBusyRef.current = true;
        // Списание мгновенное и без отката, поэтому спрашиваем, как при покупке и
        // подарке с баланса: биометрия, а без неё явный попап с суммой и сроком.
        const { biometric, confirmAction } = await import('@/lib/telegram');
        const result =
          api.getAuth().biometricConfirmEnabled === false
            ? 'unavailable'
            : await biometric.authenticate(t('Confirm purchase'));
        if (result !== 'ok') {
          const ok = await confirmAction(balanceConfirmText, t('Payment confirm title'));
          if (!ok) {
            renewBusyRef.current = false;
            return;
          }
        }
        doRenewBalance();
      };
      tg.MainButton.onClick(onMainBal);
      if (renewingBalance) {
        tg.MainButton.showProgress();
      } else {
        // hideProgress() в SDK Telegram сам включает кнопку: он идёт ДО disable(),
        // иначе кнопка без цены выглядела активной и не нажималась.
        tg.MainButton.hideProgress();
        // Пока цена пересчитывается, платить нечем: сумма на кнопке уже не та.
        if (quoteReady) tg.MainButton.enable();
        else tg.MainButton.disable();
      }
      return () => {
        tg.BackButton.offClick(onBack);
        tg.MainButton.offClick(onMainBal);
        tg.MainButton.hideProgress();
      };
    }

    setMainButtonText(
      t(autoRenewOn ? 'Renew via auto' : 'Renew via', {
        // Пока цена едет, многоточие: «ЗА  ₽» без суммы выглядело поломкой.
        price: priceDisplay ?? '…',
        provider: providerLabel(provider),
      }),
    );
    tg.MainButton.show();
    const onMain = () => {
      if (isFrozen || renewBusyRef.current || !quoteReady) return;
      renewBusyRef.current = true;
      doRenew();
    };
    tg.MainButton.onClick(onMain);
    if (isPending) {
      tg.MainButton.showProgress();
    } else {
      tg.MainButton.hideProgress();
      if (isFrozen || !quoteReady) tg.MainButton.disable();
      else tg.MainButton.enable();
    }

    return () => {
      tg.BackButton.offClick(onBack);
      tg.MainButton.offClick(onMain);
      tg.MainButton.hideProgress();
    };
  }, [navigate, provider, t, doRenew, isPending, isFrozen, isTrial, isSpecial, isLifetime, lifetimeSection, priceDisplay, quoteReady, doRenewBalance, renewingBalance, balanceConfirmText, autoRenewOn]);

  const planName = sub?.planName ?? 'VPN';
  const providers = providersData?.providers ?? [];
  const sbp = useSbpGroup(providers, provider, setProvider);

  // До ответа sub ещё null, а значит isTrial/isSpecial false — экран успевал
  // показать обычное продление и только потом переключиться на «продлить нельзя».
  // Плюс название тарифа стояло заглушкой «VPN», а способ оплаты — дефолтным.
  if (subsPending || expiredPending || plansPending) return <RenewSkeleton />;

  if (isLifetime) {
    return (
      <div className="p-4 space-y-6 pb-24">
        <h2 className="tl-h text-[32px] font-semibold leading-tight tracking-[-0.035em] text-foreground">
          {t('Renew unavailable')}
        </h2>
        <div
          className="rounded-2xl p-4 text-sm flex items-start gap-3 text-foreground"
          style={{ background: 'rgba(56,224,165,0.1)', border: '1px solid rgba(56,224,165,0.28)' }}
        >
          <Zap className="w-5 h-5 shrink-0 text-money" />
          <span>{t('Lifetime no renew')}</span>
        </div>
        <button
          type="button"
          onClick={() => navigate({ to: '/me' })}
          className="w-full calm-cta py-4 rounded-[var(--r-btn)] text-base font-bold flex items-center justify-center gap-2 active:scale-[0.98] transition-transform"
        >
          {t('Back to subscription')}
        </button>
      </div>
    );
  }

  // Триал и спецтариф продлить нельзя — показываем нотис вместо выбора провайдера.
  if (isTrial || isSpecial) {
    return (
      <div className="p-4 space-y-6 pb-24">
        <h2 className="tl-h text-[32px] font-semibold leading-tight tracking-[-0.035em] text-foreground">
          {t('Renew unavailable')}
        </h2>
        <div
          className="rounded-2xl p-4 text-sm flex items-start gap-3 text-foreground"
          style={{ background: 'rgba(56,224,165,0.1)', border: '1px solid rgba(56,224,165,0.28)' }}
        >
          <Zap className="w-5 h-5 shrink-0 text-money" />
          {isSpecial ? (
            <span>
              {t('This is')} <b>{t('Renew whitelist name')}</b>. {t('Renew whitelist note')}
            </span>
          ) : (
            <span>
              {t('This is')} <b>{t('Renew trial name')}</b>, {t('Renew trial note')}
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={() =>
            navigate({ to: '/plans', search: lifetimeSection ? { section: 'lifetime' } : {} })
          }
          className="w-full calm-cta py-4 rounded-[var(--r-btn)] text-base font-bold flex items-center justify-center gap-2 active:scale-[0.98] transition-transform"
        >
          {t('Go to paid plan')}
        </button>
      </div>
    );
  }

  return (
    <div className="p-4 space-y-6 pb-24">
      {isFrozen && (
        <div className="flex items-center gap-2 rounded-[var(--r-card)] border border-[color-mix(in_srgb,var(--t-warn)_25%,transparent)] bg-[color-mix(in_srgb,var(--t-warn)_10%,transparent)] p-4 text-sm text-[var(--t-warn)]">
          <Snowflake className="w-4 h-4 shrink-0" />
          {t('Renew frozen note')}
        </div>
      )}

      <div>
        <h2 className="tl-h text-[32px] font-semibold leading-tight tracking-[-0.035em] mb-3 text-foreground">{t('Renew title')}</h2>
        {sub ? (
          <SubscriptionCard sub={sub} mini />
        ) : (
          <Card>
            <CardContent className="p-4 flex items-center gap-3">
              <IconChip icon={Zap} tone="blue" size={42} />
              <div className="min-w-0 flex-1">
                <div className="text-[15.5px] font-semibold text-foreground">{planName}</div>
                <div className="text-sm text-[var(--t-ok)]">
                  {t('Unlimited everywhere')}
                </div>
              </div>
            </CardContent>
          </Card>
        )}
        <p className="mt-3 px-1 text-[13px] text-[var(--t-muted)]">
          {t('Renew adds to end')}
        </p>
      </div>

      <RenewPeriod
        planDays={quote?.plan_days ?? sub?.days ?? 30}
        days={days}
        customInput={customInput}
        minDays={quote?.min_days ?? 3}
        maxDays={quote?.max_days ?? 365}
        onPreset={(value) => {
          setDays(value);
          setCustomInput(value == null ? '' : String(value));
        }}
        onCustomInput={(raw) => {
          setCustomInput(raw);
          const parsed = Number.parseInt(raw, 10);
          if (Number.isFinite(parsed) && parsed > 0) setDays(parsed);
        }}
      />

      <RenewPrice
        quote={quote}
        priceLabel={priceDisplay}
        stale={quoteStale}
        pending={quoteQ.isPending}
        planDays={quote?.plan_days ?? sub?.days ?? 30}
      />

      <div>
        <SectionLabel className="mb-3">{t('Payment Method')}</SectionLabel>
        <OptionGroup>
          {balancePayable &&
            (balanceEnough ? (
              <OptionRow
                icon={Wallet}
                iconTone="blue"
                title={t('From balance')}
                selected={provider === 'balance'}
                onSelect={() => setProvider('balance')}
                trailing={<span className="text-xs text-[var(--t-ok)]">{rub(Math.floor(balanceDisplayRub))}</span>}
              />
            ) : (
              <OptionRow
                icon={Wallet}
                iconTone="neutral"
                title={t('From balance')}
                subtitle={t('Not enough topup', { amount: balanceShortfallRub })}
                onSelect={() => navigate({ to: '/topup' })}
                trailing={<span className="text-xs text-foreground">{t('Top up arrow')}</span>}
              />
            ))}
          {sbp.rows.map((p) => (
            <Fragment key={p}>
              {p === SBP_GROUP ? (
                <SbpRow group={sbp} />
              ) : (
                <OptionRow
                  leading={<PayLogo provider={p} />}
                  title={providerLabel(p)}
                  selected={provider === p}
                  onSelect={() => setProvider(p)}
                  className={sbp.isVariant(p) ? 'pl-8' : undefined}
                />
              )}
              {/* Автопродление сразу под выбранным способом, как на оформлении. */}
              {(p === provider || (p === SBP_GROUP && sbp.selected)) && recurrentShown && recurrentOffer && priceDisplay ? (
                <RecurrentToggle
                  inline
                  enabled={recurrentOn}
                  onChange={setRecurrentOn}
                  amount={`${priceDisplay}\u00a0₽`}
                  period={recurrentOffer.period}
                  termsUrl={recurrentOffer.terms_url}
                />
              ) : null}
            </Fragment>
          ))}
          {providersData && providers.length === 0 && (
            <div className="p-4 text-sm text-[var(--t-muted)]">
              {t('No providers support')}
            </div>
          )}
        </OptionGroup>
      </div>

    </div>
  );
}

/**
 * Выбор срока продления: период тарифа или свой.
 *
 * Пресеты закрывают почти все случаи, поле — остальные. Значение зажимается на бэке
 * (`clamp_client_days`), поэтому здесь не мешаем печатать: подсказка про границы
 * стоит рядом, а не выскакивает после каждого символа.
 */
function RenewPeriod({
  planDays,
  days,
  customInput,
  minDays,
  maxDays,
  onPreset,
  onCustomInput,
}: {
  planDays: number;
  days: number | null;
  customInput: string;
  minDays: number;
  maxDays: number;
  onPreset: (value: number | null) => void;
  onCustomInput: (raw: string) => void;
}) {
  const { t } = useTranslation();
  const presets = useMemo(
    () => [7, 14, 30, 60, 90].filter((d) => d >= minDays && d <= maxDays),
    [minDays, maxDays],
  );
  const custom = days !== null;

  return (
    <div>
      <SectionLabel className="mb-3">{t('Renew period')}</SectionLabel>
      <Card>
        <CardContent className="p-4 space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => onPreset(null)}
              className={`rounded-[var(--r-btn)] py-2.5 text-sm font-bold transition-colors ${
                custom ? 'bg-[var(--t-fill)] text-[var(--t-muted)]' : 'bg-foreground text-background'
              }`}
            >
              {t('Plan period days', { count: planDays })}
            </button>
            <button
              type="button"
              onClick={() => onPreset(presets[0] ?? minDays)}
              className={`rounded-[var(--r-btn)] py-2.5 text-sm font-bold transition-colors ${
                custom ? 'bg-foreground text-background' : 'bg-[var(--t-fill)] text-[var(--t-muted)]'
              }`}
            >
              {t('Custom period')}
            </button>
          </div>

          {custom && (
            <div className="space-y-3">
              <div className="flex flex-wrap gap-2">
                {presets.map((value) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => onPreset(value)}
                    className={`rounded-full px-3 py-1.5 text-xs font-bold transition-colors ${
                      days === value
                        ? 'bg-accent/20 text-foreground'
                        : 'bg-[var(--t-fill)] text-[var(--t-muted)]'
                    }`}
                  >
                    {t('N days short', { count: value })}
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-2">
                <CalendarClock className="w-4 h-4 shrink-0 text-[var(--t-muted)]" />
                <input
                  inputMode="numeric"
                  pattern="[0-9]*"
                  value={customInput}
                  onChange={(e) => onCustomInput(e.target.value.replace(/[^0-9]/g, ''))}
                  placeholder={String(planDays)}
                  className="w-20 rounded-lg bg-[var(--t-fill)] px-2 py-1.5 text-sm text-foreground outline-none"
                />
                <span className="text-xs text-[var(--t-muted)]">
                  {t('Days range hint', { min: minDays, max: maxDays })}
                </span>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

/** Цена выбранного срока и дата окончания после продления. */
function RenewPrice({
  quote,
  priceLabel,
  stale,
  pending,
  planDays,
}: {
  quote: RenewQuote | null;
  /** Цена выбранного способа оплаты: с баланса без сбора, счётом со сбором способа. */
  priceLabel: string | null;
  stale: boolean;
  pending: boolean;
  planDays: number;
}) {
  const { t, i18n } = useTranslation();
  if (pending || quote == null) {
    return (
      <Card>
        <CardContent className="p-4 space-y-2">
          <SkeletonLine className="w-1/2" />
          <SkeletonLine className="w-2/3" />
        </CardContent>
      </Card>
    );
  }
  const price = priceLabel ?? String(Math.ceil(quote.price_display_minor / 100));
  const before = Math.ceil(quote.price_before_discount_display_minor / 100);
  const endDate = new Date(quote.new_end_date).toLocaleDateString(
    i18n.language === 'en' ? 'en-GB' : 'ru-RU',
    { day: 'numeric', month: 'long' },
  );
  return (
    <Card>
      <CardContent className={`p-4 transition-opacity ${stale ? 'opacity-40' : ''}`}>
        <div className="flex items-baseline gap-2">
          <span className="text-[22px] font-semibold text-foreground">{rub(price)}</span>
          {quote.discount_display_minor > 0 && (
            <span className="text-sm text-[var(--t-muted)] line-through">{rub(before)}</span>
          )}
          <span className="ml-auto text-xs text-[var(--t-muted)]">
            {t('For N days', { count: quote.days ?? planDays })}
          </span>
        </div>
        {quote.promo_code && quote.discount_display_minor > 0 && (
          <div className="mt-1 text-xs text-[var(--t-ok)]">
            {t('Renew promo applied', { code: quote.promo_code })}
          </div>
        )}
        {quote.promo_blocked_reason === 'partner_funds' && (
          <div className="mt-1 text-xs text-[var(--t-danger)]">{t('Promo unavailable')}</div>
        )}
        <div className="mt-2 text-xs text-[var(--t-muted)]">
          {t('Active until date', { date: endDate })}
        </div>
      </CardContent>
    </Card>
  );
}

/** Карточка тарифа и выбор способа оплаты. Заголовок статичный. */
function RenewSkeleton() {
  const { t } = useTranslation();
  return (
    <SkeletonPage className="space-y-6">
      <div>
        <h2 className="tl-h text-[32px] font-semibold leading-tight tracking-[-0.035em] mb-3 text-foreground">{t('Renew title')}</h2>
        <Skeleton className="h-[148px] w-full rounded-[var(--r-subcard)]" />
      </div>
      <div>
        <SectionLabel className="mb-3">{t('Payment Method')}</SectionLabel>
        <SkeletonCard className="min-h-0 divide-y divide-[var(--t-line)] rounded-[var(--r-card)]">
          {Array.from({ length: 4 }, (_, i) => (
            <div key={i} className="flex min-h-[62px] items-center gap-3.5 px-4">
              <Skeleton className="size-9 shrink-0 rounded-[var(--r-sm)]" />
              <SkeletonLine className="w-1/3" />
              <Skeleton className="ml-auto size-[22px] shrink-0 rounded-full" />
            </div>
          ))}
        </SkeletonCard>
      </div>
    </SkeletonPage>
  );
}
